import * as React from 'react'
import useDayHistoryStore from '../../../../store/dayHistoryStore.ts'
import { interpolatePosition, subdividePath, smoothPathToWaypoints } from '../../../../helpers/dayHistoryHelper.ts'
import { IDayHistoryWaypoint } from '../../../../interfaces/dayHistory.ts'

/**
 * The playback engine for Day History.
 * Runs a requestAnimationFrame loop that advances currentTime based on playbackSpeed,
 * computes the interpolated position, and updates the store.
 *
 * This component renders nothing — it's purely a logic driver.
 */
export const DayHistoryPlayback: React.FC = () => {
    const pathData = useDayHistoryStore(s => s.pathData)
    const isPlaying = useDayHistoryStore(s => s.isPlaying)
    const playbackSpeed = useDayHistoryStore(s => s.playbackSpeed)
    const currentTime = useDayHistoryStore(s => s.currentTime)

    const rafRef = React.useRef<number | null>(null)
    const lastFrameRef = React.useRef<number>(0)

    // Use timed smoothPath for interpolation if available (dense + smooth),
    // otherwise fall back to subdivided raw waypoints
    const processedPath = React.useMemo<IDayHistoryWaypoint[]>(() => {
        if (!pathData?.path?.length) return []
        if (pathData.smoothPath) {
            return smoothPathToWaypoints(pathData.smoothPath)
        }
        return subdividePath(pathData.path)
    }, [pathData])

    // When using smoothPath, don't apply gap detection to the marker —
    // the smooth path is continuous and handles transitions naturally.
    // Only apply gaps when using raw waypoints (which have actual time gaps).
    const useSmoothPath = !!pathData?.smoothPath
    const gapsForInterpolation = useSmoothPath ? [] : (pathData?.gaps || [])

    // Initialize currentTime to start of duration when data loads
    React.useEffect(() => {
        if (pathData?.duration) {
            useDayHistoryStore.getState().setCurrentTime(pathData.duration.start)
            const pos = interpolatePosition(processedPath, gapsForInterpolation, pathData.duration.start)
            useDayHistoryStore.getState().setCurrentPosition(pos)
        }
    }, [pathData, processedPath])

    // Animation loop
    React.useEffect(() => {
        if (!isPlaying || !pathData) {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
            return
        }

        const endTime = pathData.duration.end
        const gaps = pathData.gaps

        /** How many real-time seconds to spend advancing through any gap, regardless of gap length */
        const GAP_TRAVERSE_SECONDS = 1.5

        const tick = (timestamp: number) => {
            if (!lastFrameRef.current) {
                lastFrameRef.current = timestamp
            }

            const deltaMs = timestamp - lastFrameRef.current
            lastFrameRef.current = timestamp

            const store = useDayHistoryStore.getState()
            const currentTime = store.currentTime

            // Check if we're currently in a gap — if so, use warp speed
            const activeGap = gaps.find(g => currentTime >= g.startTime && currentTime < g.endTime)
            let effectiveSpeed: number

            if (activeGap) {
                // Warp through the gap: entire gap duration in GAP_TRAVERSE_SECONDS of real time
                const gapDuration = activeGap.endTime - activeGap.startTime
                effectiveSpeed = gapDuration / GAP_TRAVERSE_SECONDS
            } else {
                effectiveSpeed = playbackSpeed
            }

            // Advance simulated time
            const deltaSimulated = (deltaMs / 1000) * effectiveSpeed
            let newTime = currentTime + deltaSimulated

            // Clamp to end of day
            if (newTime >= endTime) {
                newTime = endTime
                store.setCurrentTime(newTime)
                const pos = interpolatePosition(processedPath, gapsForInterpolation, newTime)
                store.setCurrentPosition(pos)
                store.setIsPlaying(false) // Auto-pause at end
                lastFrameRef.current = 0
                return
            }

            store.setCurrentTime(newTime)
            const pos = interpolatePosition(processedPath, gapsForInterpolation, newTime)
            store.setCurrentPosition(pos)

            rafRef.current = requestAnimationFrame(tick)
        }

        lastFrameRef.current = 0
        rafRef.current = requestAnimationFrame(tick)

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
        }
    }, [isPlaying, pathData, playbackSpeed, processedPath, gapsForInterpolation])

    // When currentTime changes externally (scrubbing), update position
    React.useEffect(() => {
        if (!pathData || isPlaying) return
        const pos = interpolatePosition(processedPath, gapsForInterpolation, currentTime)
        useDayHistoryStore.getState().setCurrentPosition(pos)
    }, [currentTime, pathData, isPlaying, processedPath])

    return null
}
